import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { RegistererState, LogLevel } from 'sip.js';
import { OutgoingRequestDelegate } from 'sip.js/lib/core';
import { SessionManager, SessionManagerDelegate } from 'sip.js/lib/platform/web';
import { createContext, useContextSelector } from 'use-context-selector';
import { SoftphoneTypes } from '@frontend/api-softphone';
import { useAppSettingsShallowStore } from '@frontend/app-settings-store';
import { useNetworkState } from '@frontend/document';
import { SoftphoneStateInterface } from '@frontend/softphone-state';
import { TAB_ID } from '@frontend/tab-management';
import { makeTargetURI } from '../../utils/phone-utils';
import { useSoftphoneEventsEmitter } from '../softphone-events-provider';
import { SoftphoneClientController, SoftphoneClientCredentials } from './softphone-client-controller';

export type SoftphoneClientContextValue = {
  uri: string;
  domain: SoftphoneTypes.SipProfile['domain'];
  client: SessionManager | undefined;
  registrationState: keyof typeof RegistererState | undefined;
  reconnect: () => void;
  disconnect: () => void;
  restart: () => void;
  status: 'loading' | 'success' | 'error';
  deviceName: string;
  extensionNumber: number | undefined;
  extensions: SoftphoneTypes.User[];
  registerCallId: string;
};

const SoftphoneClientContext = createContext<SoftphoneClientContextValue>({} as SoftphoneClientContextValue);

type SoftphoneProviderProps = {
  onConnect?: () => void;
  onDisconnect?: () => void;
  onRestart?: () => Promise<void>;
  proxy: string;
  username: string;
  domain: SoftphoneTypes.SipProfile['domain'];
  password: string;
  userAgent: string;
  children: React.ReactNode;
  deviceName: string;
  extensionNumber: number | undefined;
  extensions: SoftphoneTypes.User[];
  setError: (error: Error | undefined) => void;
};

type ConnectionPhase = 'connecting' | 'restarting' | 'connected' | 'error';

type ConnectionPhaseAction =
  | { type: 'connection-started' }
  | { type: 'restart-started' }
  | { type: 'registered' }
  | { type: 'connection-failed' }
  | { type: 'disconnected' };

const reduceConnectionPhase = (state: ConnectionPhase, action: ConnectionPhaseAction): ConnectionPhase => {
  switch (action.type) {
    case 'connection-started':
      return state === 'restarting' ? state : 'connecting';
    case 'restart-started':
      return 'restarting';
    case 'registered':
      return 'connected';
    case 'connection-failed':
      return 'error';
    case 'disconnected':
      return state === 'restarting' ? state : 'error';
    default:
      return state;
  }
};

const registrationExpiry = 300;
const refreshFrequency = 50;
const restartRecreateDelayMs = 1500;

export const SoftphoneClientProvider = ({
  proxy,
  username,
  domain,
  password,
  userAgent,
  deviceName,
  extensionNumber,
  onConnect,
  onDisconnect,
  onRestart,
  extensions,
  children,
  setError,
}: SoftphoneProviderProps) => {
  const [registrationState, setRegistrationState] = useState<keyof typeof RegistererState>();
  const [connectionPhase, dispatchConnectionPhase] = useReducer(reduceConnectionPhase, 'connecting');
  const [client, setClient] = useState<SessionManager>();
  const emitter = useSoftphoneEventsEmitter();
  const registerCallId = useRef<string>('');
  const controllerRef = useRef(new SoftphoneClientController());
  const hasStartedRef = useRef(false);
  // Always-current credential refs so restart() picks up props refreshed during onRestart().
  const proxyRef = useRef(proxy);
  const usernameRef = useRef(username);
  const domainRef = useRef(domain);
  const passwordRef = useRef(password);
  proxyRef.current = proxy;
  usernameRef.current = username;
  domainRef.current = domain;
  passwordRef.current = password;
  const { setHasActiveSoftphoneCalls } = useAppSettingsShallowStore('setHasActiveSoftphoneCalls');
  /**
   * START DELEGATE DEFINITIONS---------------------------------------------------------------
   *
   * These delegates are used to handle the various events that occur during the lifecycle of the softphone client.
   */

  const markConnectionStarted = useCallback(() => {
    dispatchConnectionPhase({ type: 'connection-started' });
    setError(undefined);
  }, [setError]);

  const markRegistered = useCallback(
    (callId?: string) => {
      setRegistrationState('Registered');
      dispatchConnectionPhase({ type: 'registered' });
      setError(undefined);

      if (callId) {
        registerCallId.current = callId;
      }
    },
    [setError]
  );

  const markConnectionFailed = useCallback(
    (error: Error) => {
      setRegistrationState('Unregistered');
      dispatchConnectionPhase({ type: 'connection-failed' });
      setError(error);
    },
    [setError]
  );

  const markDisconnected = useCallback(() => {
    registerCallId.current = '';
    setRegistrationState('Unregistered');
    dispatchConnectionPhase({ type: 'disconnected' });
  }, []);

  const markConnectionStartedRef = useRef(markConnectionStarted);
  const markConnectionFailedRef = useRef(markConnectionFailed);
  markConnectionStartedRef.current = markConnectionStarted;
  markConnectionFailedRef.current = markConnectionFailed;

  const requestDelegate: OutgoingRequestDelegate = useMemo(
    () => ({
      onAccept: (res) => {
        markRegistered(res.message.callId);
        emitter.emit('registration.registered', { callId: res.message.callId });
        /**
         * Keep track of the last registered call id when it is unregistered inside service worker which will be compared
         * with the call id received from the server to determine if the call id is the same as the last registered call id.
         * in useSoftphoneRegistrationQuery
         */
        SoftphoneStateInterface.broadcastLastRegisteredCallId({ tabId: TAB_ID, callId: res.message.callId });
        SoftphoneStateInterface.broadcastRegisterSoftphone({ tabId: TAB_ID, hasSoftphoneRegistered: true });
      },
      onReject: (res) => {
        markConnectionFailed(new Error('Registration Failure'));
        emitter.emit('registration.failure', { callId: res.message.callId });
        console.log('registration.failure', res);
        // sentry.warn({
        //   error:
        //     'Error reconnecting Softphone: ' + res
        //       ? typeof res === 'object'
        //         ? JSON.stringify(res?.message)
        //         : JSON.stringify(res)
        //       : 'Unknown',
        //   topic: 'phone',
        //   team: 'phones',
        // });
      },
    }),
    [emitter, markConnectionFailed, markRegistered]
  );

  const sessionManagerDelegate: SessionManagerDelegate = useMemo(
    () => ({
      onServerDisconnect: (err) => {
        console.log('Phone Disconnected', err, { registerCallId: registerCallId.current });
        /**
         * Preemptively set the registration state to Unregistered, because SIPjs takes a long time to unregister.
         */

        SoftphoneStateInterface.broadcastRegisterSoftphone({ tabId: TAB_ID, hasSoftphoneRegistered: false });
        setHasActiveSoftphoneCalls(false);
        SoftphoneStateInterface.broadcastActiveCall({ tabId: TAB_ID, hasActiveCall: false });
        emitter.emit('registration.unregistered', { callId: registerCallId.current });
        markDisconnected();
        onDisconnect?.();
      },
      onRegistered() {
        markRegistered();
        onConnect?.();
      },
      onUnregistered() {
        /**
         * SIPjs deliberately takes a long time to unregister, so we cannot rely on this hook to
         * set any sort of UI state that should be immediate.
         *
         * Anything immediate should be moved to `onServerDisconnect`.
         */
      },
      onNotificationReceived(request) {
        if (request.request.headers['O'][0].raw === 'check-sync') {
          request.accept();
        }
      },
      onMessageReceived(message) {
        console.log('Message Received', message);
      },
    }),
    [emitter, markDisconnected, markRegistered, onConnect, onDisconnect, setHasActiveSoftphoneCalls]
  );

  const createSessionManager = useCallback(
    ({ proxy, username, domain, password }: SoftphoneClientCredentials) => {
      const session = initSessionManager({
        proxy,
        username,
        domain,
        password,
        userAgent,
        logLevel: (localStorage.getItem('softphone.log-level') as LogLevel) ?? 'error',
      });
      session.delegate = sessionManagerDelegate;
      return session;
    },
    [sessionManagerDelegate, userAgent]
  );

  const createSessionManagerRef = useRef(createSessionManager);
  const requestDelegateRef = useRef(requestDelegate);
  createSessionManagerRef.current = createSessionManager;
  requestDelegateRef.current = requestDelegate;

  /**
   * END DELEGATE DEFINITIONS---------------------------------------------------------------
   */

  /**
   * START CONNECTION HANDLERS--------------------------------------------------------------
   *
   * These connection handlers bind the SessionManager with the provider state
   */

  const restart = useCallback(async () => {
    /**
     * What happens we restart the softphone?
     * 1. Fetch the latest softphone settings
     * 2. Disconnect the SessionManager
     * 3. Create a new SessionManager and connect to the server
     *
     * There should only be one websocket connection open at a time.
     *
     * Credentials are read from refs (not the closed-over props) so that any
     * settings refreshed by onRestart() are picked up when creating the new session.
     */
    const restartingCallId = registerCallId.current;

    dispatchConnectionPhase({ type: 'restart-started' });
    setError(undefined);
    await onRestart?.();

    const currentClient = controllerRef.current.getClient();
    if (currentClient) {
      await controllerRef.current.disconnect({
        session: currentClient,
        onBeforeDisconnect: () => {
          setRegistrationState(RegistererState.Terminated);
        },
      });
    }

    controllerRef.current.setClient(undefined);
    setClient(undefined);

    await new Promise((resolve) => {
      setTimeout(resolve, restartRecreateDelayMs);
    });

    // Read credentials from refs so we get the values from the latest render
    // (which may have changed during the await onRestart() above).
    const latestProxy = proxyRef.current;
    const latestUsername = usernameRef.current;
    const latestDomain = domainRef.current;
    const latestPassword = passwordRef.current;

    if (latestProxy && latestUsername && latestDomain && latestPassword) {
      controllerRef.current.createSession({
        credentials: {
          proxy: latestProxy,
          username: latestUsername,
          domain: latestDomain,
          password: latestPassword,
        },
        createSessionManager: createSessionManagerRef.current,
        onConnecting: markConnectionStartedRef.current,
        onConnectionFailed: markConnectionFailedRef.current,
        onClientReady: setClient,
        requestDelegate: requestDelegateRef.current,
      });
    }

    if (restartingCallId) {
      emitter.emit('softphone.restart', { callID: restartingCallId });
    }
  }, [emitter, onRestart, setError]);

  /**
   * END CONNECTION HANDLERS--------------------------------------------------------------
   */

  /**
   * Start the softphone once when credentials first become available.
   * After that, restart() owns any full session recreation explicitly.
   */
  useEffect(() => {
    if (!(proxy && username && domain && password) || hasStartedRef.current) {
      return;
    }

    hasStartedRef.current = true;
    controllerRef.current.createSession({
      credentials: {
        proxy,
        username,
        domain,
        password,
      },
      createSessionManager: createSessionManagerRef.current,
      onConnecting: markConnectionStartedRef.current,
      onConnectionFailed: markConnectionFailedRef.current,
      onClientReady: setClient,
      requestDelegate: requestDelegateRef.current,
    });
  }, [domain, password, proxy, username]);

  // Update credentials on the live session when they change, without recreating it.
  useEffect(() => {
    if (client) {
      client.userAgent.configuration.authorizationPassword = password;
      client.userAgent.configuration.authorizationUsername = username;
    }
  }, [client, password, username]);

  useEffect(() => {
    controllerRef.current.setClient(client);
  }, [client]);

  // Disconnect on unmount.
  useEffect(() => {
    const controller = controllerRef.current;

    return () => {
      const currentClient = controller.getClient();
      if (currentClient?.isConnected()) {
        void controller.disconnect({
          session: currentClient,
          onBeforeDisconnect: () => {
            setRegistrationState(RegistererState.Terminated);
          },
        });
      }
    };
  }, []);

  const onOnline = () => {
    const currentClient = controllerRef.current.getClient();
    if (currentClient) {
      const reconnectPromise = controllerRef.current.connect({
        session: currentClient,
        onConnecting: markConnectionStarted,
        onConnectionFailed: markConnectionFailed,
        requestDelegate: requestDelegateRef.current,
      });
      if (reconnectPromise) {
        void reconnectPromise.catch(() => undefined);
      }
    }
  };

  const onOffline = () => {
    setError(new Error('No internet connection'));

    const currentClient = controllerRef.current.getClient();

    if (currentClient) {
      currentClient.delegate = {
        ...currentClient.delegate,
        onServerDisconnect: () => {
          // Do nothing if disconnected while offline
        },
      };
      void controllerRef.current.disconnect({
        session: currentClient,
        onBeforeDisconnect: () => {
          setRegistrationState(RegistererState.Terminated);
        },
      });
    }
  };

  useNetworkStateHandler({
    onOnline,
    onOffline,
  });

  const status = (() => {
    if (connectionPhase === 'connecting' || connectionPhase === 'restarting') {
      return 'loading';
    }

    if (connectionPhase === 'connected' && client?.isConnected() && registrationState === 'Registered') {
      return 'success';
    }

    return 'error';
  })();

  useEffect(() => {
    /**
     * If the user navigates away from the page, we need to disconnect the softphone client.
     */
    const onUnloadFn = () => {
      const currentClient = controllerRef.current.getClient();
      if (currentClient) {
        void controllerRef.current.disconnect({
          session: currentClient,
          onBeforeDisconnect: () => {
            setRegistrationState(RegistererState.Terminated);
          },
        });
      }
    };

    window.addEventListener('pagehide', onUnloadFn);

    return () => {
      window.removeEventListener('pagehide', onUnloadFn);
    };
  }, []);

  useEffect(() => {
    /**
     * When another browser tab becomes the active softphone tab (via activateCurrentTab),
     * the tab management layer dispatches this event so this tab disconnects its WebSocket
     * immediately, preventing two tabs from holding simultaneous SIP registrations.
     */
    const onDeactivate = () => {
      const currentClient = controllerRef.current.getClient();
      if (currentClient) {
        void controllerRef.current.disconnect({
          session: currentClient,
          onBeforeDisconnect: () => {
            setRegistrationState(RegistererState.Terminated);
          },
        });
      }
    };

    window.addEventListener('softphone:deactivate', onDeactivate);

    return () => {
      window.removeEventListener('softphone:deactivate', onDeactivate);
    };
  }, []);

  const value = useMemo(
    () =>
      ({
        uri: makeTargetURI(`${username}@${domain}`)?.toString() ?? '',
        domain,
        client,
        registrationState,
        reconnect: () => {
          const currentClient = controllerRef.current.getClient();
          if (currentClient) {
            const reconnectPromise = controllerRef.current.connect({
              session: currentClient,
              onConnecting: markConnectionStarted,
              onConnectionFailed: markConnectionFailed,
              requestDelegate: requestDelegateRef.current,
            });
            if (reconnectPromise) {
              void reconnectPromise.catch(() => undefined);
            }
          }
        },
        disconnect: () => {
          const currentClient = controllerRef.current.getClient();
          if (currentClient) {
            void controllerRef.current.disconnect({
              session: currentClient,
              onBeforeDisconnect: () => {
                setRegistrationState(RegistererState.Terminated);
              },
            });
          }
        },
        restart,
        deviceName,
        extensionNumber,
        extensions,
        status,
        registerCallId: registerCallId.current,
      } as const satisfies SoftphoneClientContextValue),
    [
      username,
      domain,
      client,
      registrationState,
      restart,
      deviceName,
      extensionNumber,
      extensions,
      status,
      markConnectionFailed,
      markConnectionStarted,
    ]
  );

  return <SoftphoneClientContext.Provider value={value}>{children}</SoftphoneClientContext.Provider>;
};

export const useSoftphoneClient = <T extends any>(selector: (val: SoftphoneClientContextValue) => T) => {
  return useContextSelector(SoftphoneClientContext, selector);
};

const useNetworkStateHandler = ({ onOnline, onOffline }: { onOnline: () => void; onOffline: () => void }) => {
  const { online } = useNetworkState();
  const onlineState = useRef(true);

  useEffect(() => {
    if (onlineState.current && !online) {
      onOffline();
    } else if (!onlineState.current && online) {
      onOnline();
    }
    onlineState.current = online;
  }, [online, onlineState, onOnline, onOffline]);

  return online;
};

/**
 * This initialize function is INDEPENDENT of any context.
 * Delegates that require provider context can be set after initialization.
 */
function initSessionManager({
  proxy,
  username,
  domain,
  password,
  userAgent,
  logLevel,
}: {
  proxy: string;
  username: string;
  domain: SoftphoneTypes.FullyQualifiedAddress;
  password: string;
  userAgent: string;
  logLevel: LogLevel;
}) {
  const session = new SessionManager(`wss://${proxy}`, {
    media: {
      constraints: {
        audio: true,
        video: false,
      },
    },
    registererOptions: {
      expires: registrationExpiry,
      refreshFrequency: refreshFrequency,
    },
    maxSimultaneousSessions: 5,
    autoStop: false,
    userAgentOptions: {
      uri: makeTargetURI(`${username}@${domain}`),
      logLevel,
      userAgentString: userAgent,
      authorizationPassword: password,
      authorizationUsername: username,
      sessionDescriptionHandlerFactoryOptions: {
        iceCheckingTimeout: 2000,
        peerConnectionConfiguration: {
          iceServers: [],
        },
      },
    },
  });

  return session;
}
